import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import axios from '../api';
import Chip from '@mui/material/Chip';
import { CATEGORY_CONDITION_LEAF_IDS } from '../categoryConditionLeafIds';
import Snackbar from './Snackbar';

// Convert all digits in a string/number to Farsi
function toFarsiNumber(input) {
  return String(input).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
}

// Format a number as price with thousands separator and Farsi digits
function toFarsiPrice(num) {
  if (num === undefined || num === null || num === '' || isNaN(num)) return '-';
  return toFarsiNumber(Number(num).toLocaleString('fa-IR'));
}

function getRelativeTime(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) {
    return 'چند لحظه پیش';
  }
  if (diffHours < 1) {
    return `${diffMins} دقیقه پیش`;
  }
  if (diffDays < 1) {
    return `${diffHours} ساعت پیش`;
  }
  if (diffDays < 7) {
    return `${diffDays} روز پیش`;
  }
  return date.toLocaleDateString('fa-IR');
}
const AdCard = ({ ad }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginWarn, setLoginWarn] = useState(false);

  useEffect(() => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch {
        setCurrentUser(null);
      }
    }
  }, []);

  useEffect(() => {
    if (!ad || !currentUser) return;
    // Check if ad is saved by current user
    if (ad.saved_by && Array.isArray(ad.saved_by)) {
      setSaved(ad.saved_by.includes(currentUser.id));
    }
  }, [ad, currentUser]);

  const handleSaveToggle = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      setLoginWarn(true);
      return;
    }
    try {
      if (saved) {
        await axios.post('/ads/unsave/', { ad_id: ad.id });
      } else {
        await axios.post('/ads/save/', { ad_id: ad.id });
      }
      // Reload ad to get latest saved status from backend
      const res = await axios.get(`/ads/${ad.id}/`);
      if (res.data.saved_by && Array.isArray(res.data.saved_by)) {
        setSaved(res.data.saved_by.includes(currentUser.id));
      }
    } catch {
      // Optionally show error
    }
  };

  const handleClick = () => {
    navigate(`/ads/${ad.id}`);
  };
  // Swipeable images
  const [imgIdx, setImgIdx] = useState(0);
  const images = Array.isArray(ad.images) ? ad.images : [];
  const showImage = images.length > 0 ? images[imgIdx].image : '';
  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        direction: 'rtl',
        border: 1,
        borderColor: 'grey.100',
        p: 2,
        cursor: 'pointer',
        position: 'relative',
        maxHeight: { xs: 370, md: 392 },
        height: { xs: 370, md: 392 },
        overflow: 'hidden',
      }}
      onClick={handleClick}
    >
      <IconButton
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          bgcolor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '50%',
          boxShadow: 1,
          width: 40,
          height: 40,
          p: 0,
        }}
        onClick={handleSaveToggle}
        aria-label={saved ? 'حذف از نشان‌شده‌ها' : 'نشان کردن'}
      >
        {saved ? <BookmarkIcon color="primary" /> : <BookmarkBorderIcon color="action" />}
      </IconButton>
      <Box sx={{ position: 'relative', mb: 2 }}>
        <CardMedia
          component="img"
          height="192"
          image={showImage}
          alt={ad.title}
          sx={{ borderRadius: 2, objectFit: 'cover', bgcolor: 'grey.50' }}
        />
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
            {images.map((img, idx) => (
              <Box key={img.id} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: imgIdx === idx ? 'primary.main' : 'grey.300', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setImgIdx(idx); }} />
            ))}
          </Box>
        )}
      </Box>
      <CardContent sx={{ p: 0 }}>
        <Typography variant="h6" component="div" gutterBottom sx={{
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minHeight: '1.5em',
          maxHeight: '1.5em',
        }}>
          {toFarsiNumber(ad.title)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minHeight: '1.3em',
          maxHeight: '1.3em',
        }} align="right">
          {toFarsiNumber(ad.description)}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
          {CATEGORY_CONDITION_LEAF_IDS.includes(Number(ad.category)) && ad.condition && (
            <Chip label={toFarsiNumber(ad.condition)} size="small" sx={{ bgcolor: 'blue.50', color: 'blue.700', fontWeight: 'bold', fontFamily: 'Vazirmatn' }} />
          )}
          {ad.category == 3 ? (
            <>
              <Chip label={`ودیعه: ${toFarsiPrice(ad.deposit)} تومان`} size="medium" sx={{ bgcolor: 'orange.50', color: 'orange.700', fontWeight: 'bold' }} />
              <Chip label={`اجاره: ${toFarsiPrice(ad.monthly_rent)} تومان`} size="medium" sx={{ bgcolor: 'green.50', color: 'green.700', fontWeight: 'bold' }} />
            </>
          ) : (
            <Chip label={toFarsiPrice(ad.price) + ' تومان'} size="medium" sx={{ bgcolor: 'green.50', color: 'green.700', fontWeight: 'bold' }} />
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" align="right" sx={{ mt: 2, display: 'block' }}>
          {toFarsiNumber(getRelativeTime(ad.date_posted))}
        </Typography>
      </CardContent>
      <Snackbar
        open={loginWarn}
        onClose={() => setLoginWarn(false)}
        message="برای نشان کردن آگهی ابتدا وارد حساب کاربری شوید"
        severity="warning"
        autoHideDuration={3000}
      />
    </Card>
  );
};

export default AdCard;
